import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TagManagement.css';

function TagManagement({ onBack }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameTag = async (oldName) => {
    if (!newTagName.trim() || newTagName === oldName) {
      setEditingTag(null);
      setNewTagName('');
      return;
    }

    try {
      await axios.put(`/api/tags/${encodeURIComponent(oldName)}`, { newName: newTagName.trim() });
      await fetchTags();
      setEditingTag(null);
      setNewTagName('');
      setError('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to rename tag');
    }
  };

  const handleDeleteTag = async (tagName) => {
    if (!window.confirm(`Delete tag "${tagName}"? This will remove it from all bookmarks.`)) {
      return;
    }

    try {
      await axios.delete(`/api/tags/${encodeURIComponent(tagName)}`);
      await fetchTags();
      setError('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete tag');
    }
  };

  const startEdit = (tag) => {
    setEditingTag(tag.name);
    setNewTagName(tag.name);
    setError('');
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setNewTagName('');
    setError('');
  };

  return (
    <div className="tag-management">
      <div className="tag-management-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Bookmarks
        </button>
        <h2>Tag Management</h2>
        <p>Manage all your tags in one place</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading">Loading tags...</div>
      ) : tags.length === 0 ? (
        <div className="no-tags">
          No tags yet. Tags will appear here when you add them to bookmarks.
        </div>
      ) : (
        <div className="tags-table">
          <div className="table-header">
            <div className="col-name">Tag Name</div>
            <div className="col-count">Used In</div>
            <div className="col-actions">Actions</div>
          </div>
          {tags.map((tag, index) => (
            <div key={index} className="table-row">
              <div className="col-name">
                {editingTag === tag.name ? (
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameTag(tag.name);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="tag-edit-input"
                    autoFocus
                  />
                ) : (
                  <span className="tag-name-display">{tag.name}</span>
                )}
              </div>
              <div className="col-count">
                <span className="count-badge">{tag.count} bookmark{tag.count !== 1 ? 's' : ''}</span>
              </div>
              <div className="col-actions">
                {editingTag === tag.name ? (
                  <>
                    <button
                      className="btn-save"
                      onClick={() => handleRenameTag(tag.name)}
                      title="Save"
                    >
                      ✓
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={cancelEdit}
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn-edit"
                      onClick={() => startEdit(tag)}
                      title="Rename tag"
                    >
                      ✏️ Rename
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteTag(tag.name)}
                      title="Delete tag"
                    >
                      🗑️ Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagManagement;
